import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import axios from 'axios';

export interface EmailPreference {
    emails: AdditionalEmail[];
    code: string;
    title: string;
    expiryDate?: Date;
}

export interface AdditionalEmail {
    email: string;
    name: string;
}

interface UserPreferencesState {
    additionalEmails: AdditionalEmail[];
    additionalEmailsLoading: boolean;
    savePreferencesLoading: boolean;
    savePreferencesError: boolean;
    savePreferencesSuccess: boolean;
    draftAdditionalEmails?: AdditionalEmail[];
    draftIssueEmailPreferences?: EmailPreference[];
    draftCommitteeEmailPreferences?: EmailPreference[];
    draftSectorEmailPreferences?: EmailPreference[];
    draftInsiderEmailPreferences?: AdditionalEmail[];
};

const initialState: UserPreferencesState = {
    additionalEmails: [],
    additionalEmailsLoading: false,
    savePreferencesLoading: false,
    savePreferencesError: false,
    savePreferencesSuccess: false,
    draftAdditionalEmails: [],
    draftIssueEmailPreferences: [],
    draftCommitteeEmailPreferences: [],
    draftSectorEmailPreferences: [],
    draftInsiderEmailPreferences: [],
};

const baseURI = process.env.REACT_APP_API_BASE_URL;

// TODO move to customSelectionSlice
export const getAdditionalEmails = createAsyncThunk(
    'user/getAdditionalEmails',
    async () => {
        const response = await axios.get(`${baseURI}/user/emails`);
        return response.data;
    }
);

export const setAdditionalEmails = createAsyncThunk(
    'user/setAdditionalEmails',
    async (additionalEmails: AdditionalEmail[]) => {
        await axios.post(`${baseURI}/user/emails`, { additionalEmails });
    }
);

export const getIssueEmailPreferences = createAsyncThunk(
    'user/getIssueEmailPreferences',
    async () => {
        const response = await axios.get(`${baseURI}/preferences/issue`);
        return response.data;
    }
);

export const getCommitteeEmailPreferences = createAsyncThunk(
    'user/getCommitteeEmailPreferences',
    async () => {
        const response = await axios.get(`${baseURI}/preferences/committee`);
        return response.data;
    }
);

export const getSectorEmailPreferences = createAsyncThunk(
    'user/getSectorEmailPreferences',
    async () => {
        const response = await axios.get(`${baseURI}/preferences/sector`);
        return response.data;
    }
);

export const getInsiderEmailPreferences = createAsyncThunk(
    'user/getInsiderEmailPreferences',
    async () => {
        const response = await axios.get(`${baseURI}/preferences/insider`);
        return response.data;
    }
);

export const setEmailPreferences = createAsyncThunk(
    'user/setEmailPreferences',
    async (_, thunk) => {
        const state = thunk.getState() as RootState;
        const additionalEmails = selectDraftAdditionalEmails(state)?.filter((email) => email.email !== '');
        const issueEmailPreferences = selectDraftIssuePreferences(state);
        const committeeEmailPreferences = selectDraftCommitteePreferences(state);
        const sectorEmailPreferences = selectDraftSectorPreferences(state);
        const insiderEmailPreferences = selectDraftInsiderPreferences(state);
        await axios.post(`${baseURI}/preferences/`, { additionalEmails, issueEmailPreferences, committeeEmailPreferences, sectorEmailPreferences, insiderEmailPreferences });
    }
);

const userPreferencesSlice = createSlice({
    name: 'userPreferences',
    initialState,
    reducers: {
        setDraftAdditionalEmails(state, action: PayloadAction<AdditionalEmail[]>) {
            state.draftAdditionalEmails = action.payload;
        },
        setDraftIssueEmailPreferences(state, action: PayloadAction<EmailPreference[]>) {
            state.draftIssueEmailPreferences = action.payload;
        },
        setDraftCommitteeEmailPreferences(state, action: PayloadAction<EmailPreference[]>) {
            state.draftCommitteeEmailPreferences = action.payload;
        },
        setDraftSectorEmailPreferences(state, action: PayloadAction<EmailPreference[]>) {
            state.draftSectorEmailPreferences = action.payload;
        },
        setDraftInsiderEmailPreferences(state, action: PayloadAction<AdditionalEmail[]>) {
            state.draftInsiderEmailPreferences = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getAdditionalEmails.pending, (state) => {
            state.additionalEmailsLoading = true;
        });
        builder.addCase(getAdditionalEmails.fulfilled, (state, action) => {
            state.additionalEmails = action.payload;
            state.draftAdditionalEmails = action.payload;
            state.additionalEmailsLoading = false;
        });
        builder.addCase(getIssueEmailPreferences.fulfilled, (state, action) => {
            state.draftIssueEmailPreferences = action.payload;
        });
        builder.addCase(getCommitteeEmailPreferences.fulfilled, (state, action) => {
            state.draftCommitteeEmailPreferences = action.payload;
        });
        builder.addCase(getSectorEmailPreferences.fulfilled, (state, action) => {
            state.draftSectorEmailPreferences = action.payload;
        });
        builder.addCase(getInsiderEmailPreferences.fulfilled, (state, action) => {
            state.draftInsiderEmailPreferences = action.payload;
        });
        builder.addCase(setEmailPreferences.pending, (state) => {
            state.savePreferencesLoading = true;
            state.savePreferencesError = false;
            state.savePreferencesSuccess = false;
        });
        builder.addCase(setEmailPreferences.fulfilled, (state) => {
            state.savePreferencesLoading = false;
            state.savePreferencesSuccess = true;
        });
        builder.addCase(setEmailPreferences.rejected, (state) => {
            state.savePreferencesLoading = false;
            state.savePreferencesError = true;
        });
    },
});

export const { setDraftAdditionalEmails, setDraftIssueEmailPreferences, setDraftCommitteeEmailPreferences, setDraftSectorEmailPreferences, setDraftInsiderEmailPreferences } = userPreferencesSlice.actions;

export const selectAdditionalEmails = (state: RootState) => state.userPreferences.additionalEmails;
export const selectDraftAdditionalEmails = (state: RootState) => state.userPreferences.draftAdditionalEmails;
export const selectDraftIssuePreferences = (state: RootState) => state.userPreferences.draftIssueEmailPreferences;
export const selectDraftCommitteePreferences = (state: RootState) => state.userPreferences.draftCommitteeEmailPreferences;
export const selectDraftSectorPreferences = (state: RootState) => state.userPreferences.draftSectorEmailPreferences;
export const selectDraftInsiderPreferences = (state: RootState) => state.userPreferences.draftInsiderEmailPreferences;
export const selectSavePreferencesLoading = (state: RootState) => state.userPreferences.savePreferencesLoading;
export const selectSavePreferencesError = (state: RootState) => state.userPreferences.savePreferencesError;
export const selectSavePreferencesSuccess = (state: RootState) => state.userPreferences.savePreferencesSuccess;

export default userPreferencesSlice.reducer;